import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import {
  Text,
  GreyGrey80,
  Primary13SemiBold,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { CustomerSelect } from 'modules/Customer'
import { useCreateInvoiceWithCustomerId } from 'Invoices/hooks/useCreateInvoiceWithCustomerId'
import { Step } from '../../components'
import { currentEditorStepAtom } from '../../editor.atoms'
import { InvoiceCustomerCard } from './InvoiceCustomerCard'
import {
  invoiceCustomerIdAtom,
  invoiceIdAtom,
  isSavePendingAtom,
  lineItemGroupsAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { CurrencySelector } from './CurrencySelector'
import { usePrefetchAllProducts } from 'common/hooks'
import { useNotifications } from '@sequencehq/core-components'
import {
  CurrentInvoiceBillingPeriodPicker,
  DropdownAddLineItemGroup,
  lineItemEditorConfig,
  SectionLineItems
} from 'Invoices/components/CreateEditShared'
import { useCloseInvoiceEditor } from 'Invoices/hooks/useCloseInvoiceEditor'

export const StepCustomerAndLineItems = () => {
  const { displayNotification } = useNotifications()

  const customerId = useAtomValue(invoiceCustomerIdAtom)
  const invoiceId = useAtomValue(invoiceIdAtom)
  const clientLineItemGroups = useAtomValue(lineItemGroupsAtom)
  const lineItemGroupsCount = clientLineItemGroups?.length ?? 0

  const createInvoiceWithCustomerId = useCreateInvoiceWithCustomerId()

  const [isCreating, setIsCreating] = useState(false)
  const [pendingCustomerId, setPendingCustomerId] = useState<
    string | undefined
  >()

  const handleChooseInitialCustomer = async (selectedCustomerId: string) => {
    if (!invoiceId) {
      setPendingCustomerId(selectedCustomerId)
      setIsCreating(true)
      try {
        await createInvoiceWithCustomerId(selectedCustomerId)
      } catch {
        displayNotification('Error creating draft invoice', {
          type: 'error'
        })
      } finally {
        setIsCreating(false)
      }
    }
  }

  usePrefetchAllProducts()

  return (
    <Step title="Invoice details" controls={<Controls />}>
      <Step.Section>
        <Flex flexDir="column" gap="8px">
          <Text {...Primary13SemiBold} color={GreyGrey80}>
            Customer
          </Text>

          {customerId ? (
            <InvoiceCustomerCard customerId={customerId} />
          ) : (
            <CustomerSelect
              onChange={handleChooseInitialCustomer}
              value={pendingCustomerId ?? customerId}
              isLoading={isCreating}
              initiallyOpen
            />
          )}
        </Flex>
      </Step.Section>

      {invoiceId && (
        <>
          <Step.Section>
            <Grid gridTemplateColumns="repeat(2, 1fr)" gap="16px">
              <GridItem>
                <CurrencySelector />
              </GridItem>

              <GridItem>
                <Flex flex="1" flexDir="column" gap="8px">
                  <Text {...Primary13SemiBold} color={GreyGrey80}>
                    Billing period
                  </Text>

                  <CurrentInvoiceBillingPeriodPicker />
                </Flex>
              </GridItem>
            </Grid>
          </Step.Section>

          <Step.Section title="Items" titleSpacing="small">
            <SectionLineItems
              tableFooter={
                <DropdownAddLineItemGroup
                  showDropdownInitially={lineItemGroupsCount === 0}
                />
              }
            />
          </Step.Section>
        </>
      )}
    </Step>
  )
}

const Controls = () => {
  const closeInvoiceEditor = useCloseInvoiceEditor()

  const setStep = useSetAtom(currentEditorStepAtom)
  const clientLineItemGroups = useAtomValue(lineItemGroupsAtom)
  const config = useAtomValue(lineItemEditorConfig)

  const isLineItemEditorVisible = !!config
  const hasNoLineItems =
    !clientLineItemGroups || clientLineItemGroups.length === 0
  const isSavePending = useAtomValue(isSavePendingAtom)

  return (
    <Step.Controls
      back={
        <Step.Controls.Back
          onClick={closeInvoiceEditor}
          label="Cancel"
          disabled={isSavePending || isLineItemEditorVisible}
        />
      }
      forward={
        <Step.Controls.Forward
          onClick={() => setStep('TERMS')}
          label="Payment details"
          keyboardShortcut={['Mod', ']']}
          disabled={isSavePending || isLineItemEditorVisible || hasNoLineItems}
        />
      }
    />
  )
}
